import JSZip from 'jszip';

export type FileSystemFileEntry = {
  isFile: true;
  isDirectory: false;
  name?: string;
  fullPath?: string;
  file: (
    successCallback: (file: File) => void,
    errorCallback?: (error: DOMException) => void
  ) => void;
};

export type FileSystemDirectoryReader = {
  readEntries: (
    successCallback: (entries: FileSystemEntry[]) => void,
    errorCallback?: (error: DOMException) => void
  ) => void;
};

export type FileSystemDirectoryEntry = {
  isFile: false;
  isDirectory: true;
  name: string;
  fullPath?: string;
  createReader: () => FileSystemDirectoryReader;
};

export type FileSystemEntry = FileSystemFileEntry | FileSystemDirectoryEntry;

const isFileEntry = (entry: FileSystemEntry): entry is FileSystemFileEntry => entry.isFile;
const isDirectoryEntry = (entry: FileSystemEntry): entry is FileSystemDirectoryEntry => entry.isDirectory;

export const getFilesFromEntry = async (entry: FileSystemEntry): Promise<File[]> => {
  if (isFileEntry(entry)) {
    return new Promise<File[]>((resolve, reject) => {
      entry.file(
        (file) => resolve([file]),
        (error) => reject(error)
      );
    });
  }

  if (isDirectoryEntry(entry)) {
    return new Promise<File[]>((resolve, reject) => {
      const reader = entry.createReader();
      const readBatch = () =>
        new Promise<FileSystemEntry[]>((batchResolve, batchReject) => {
          reader.readEntries(batchResolve, batchReject);
        });

      const readAll = async () => {
        const allEntries: FileSystemEntry[] = [];

        while (true) {
          const entries = await readBatch();
          if (entries.length === 0) {
            break;
          }
          allEntries.push(...entries);
        }

        const nestedFiles = await Promise.all(allEntries.map(getFilesFromEntry));
        resolve(nestedFiles.flat());
      };

      readAll().catch(reject);
    });
  }

  return [];
};

export const isCsvFile = (file: File): boolean =>
  file.name.toLowerCase().endsWith('.csv') && !file.name.startsWith('._');

export const isZipFile = (file: File): boolean =>
  file.name.toLowerCase().endsWith('.zip') && !file.name.startsWith('._');

export const filterCsvFiles = (files: File[]): File[] => files.filter(isCsvFile);

export const calculateTotalSize = (files: File[]): number =>
  files.reduce((sum, file) => sum + file.size, 0);

const normalizeZipPath = (file: File): string => {
  const relativePath = (file as File & { webkitRelativePath?: string }).webkitRelativePath;
  const rawPath = relativePath || file.name;
  const parts = rawPath
    .replace(/\\/g, '/')
    .split('/')
    .filter((part) => part && part !== '.' && part !== '..');
  return parts.join('/') || file.name;
};

const createUniqueZipPath = (path: string, usedPaths: Set<string>): string => {
  const slashIndex = path.lastIndexOf('/');
  const directory = slashIndex >= 0 ? `${path.slice(0, slashIndex + 1)}` : '';
  const basename = slashIndex >= 0 ? path.slice(slashIndex + 1) : path;
  const dotIndex = basename.lastIndexOf('.');
  const name = dotIndex > 0 ? basename.slice(0, dotIndex) : basename;
  const extension = dotIndex > 0 ? basename.slice(dotIndex) : '';

  let candidate = path;
  let suffix = 2;
  while (usedPaths.has(candidate)) {
    candidate = `${directory}${name} (${suffix})${extension}`;
    suffix += 1;
  }
  usedPaths.add(candidate);
  return candidate;
};

export const createZipFromFiles = async (
  files: File[],
  onProgress?: (percentage: number) => void,
): Promise<Blob> => {
  const zip = new JSZip();
  const usedPaths = new Set<string>();
  files.forEach((file) => {
    zip.file(createUniqueZipPath(normalizeZipPath(file), usedPaths), file);
  });
  return zip.generateAsync(
    { type: 'blob' },
    ({ percent }) => onProgress?.(Math.min(Math.max(percent, 0), 100)),
  );
};
